import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import RadioButtonGroup from "../../elements/radio-button-group";
import Icon from "../../elements/icon";
import Heading from "../../elements/heading";
import api from "../../utils/api";
import { useDispatch, useSelector } from "react-redux";
import "./style.scss";
import AccountRemoveAddressModal from "../account-remove-address-modal";
import PdpBillingInfoForm from "../pdp-billing-info-form";
import { isLoggedIn } from "../../services/siteService";
import { SuperButton } from "../../elements";
import { formatSavedAddress } from "../../utils/siteUtils";
import AddressInformation from "../address-information";
import { pdpBillingSelector } from "../../selectors/pdp";

const PdpBillingInfo = React.forwardRef(({ fields, userIdentity, addressList, addressLoaded, noAddress, marketCountry, loadAddressList, addAddress, updateAddress, removeAddress, visible }, ref) => {
  /*Redux start*/
  const billingFormData = useSelector(pdpBillingSelector);
  /*Redux end*/
  const { headingType, heading, addressUnsavedMessage, addressMissingMessage, removeAddressModal, ctaNameChange, ctaTypeChange, colorSchemeChange, iconNameChange } = fields;
  /**
   * @type {React.MutableRefObject<HTMLElement|NULL>}
   */
  const componentRoot = useRef();
  /**
   * @type {React.MutableRefObject<PdpEditAddressFormRef|NULL>}
   */
  const addAddressRef = useRef();
  /**
   * @type {React.MutableRefObject<PdpEditAddressFormRef|NULL>}
   */
  const editAddressRef = useRef(null);
  const [defaultAddress, setDefaultAddress] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressToRemove, setAddressToRemove] = useState(null);
  const [showRemoveAddressModal, setShowRemoveAddressModal] = useState(null);
  const [editAddress, setEditAddress] = useState(null);
  const [addressEditing, setAddressEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  useImperativeHandle(ref, () => ({
    validateForm() {
      if (isLoggedIn() && !noAddress) {
        if (editAddress) {
          editAddressRef.current.setError(addressUnsavedMessage.value);
          editAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        } else if (selectedAddress) {
          // debugger;
          if (selectedAddress.address) {
            return {
              isValid: true,
              formData: selectedAddress.address
            };
          } else {
            addAddressRef.current.setError(addressUnsavedMessage.value);
            addAddressRef.current.getEl().scrollIntoView({ behavior: "smooth" });
            return {
              isValid: false,
              formData: null
            };
          }
        } else {
          setFormError(addressMissingMessage.value);
          componentRoot.current.scrollIntoView({ behavior: "smooth" });
          return {
            isValid: false,
            formData: null
          };
        }
      } else {
        return addAddressRef.current?.validateForm();
      }
    },
    startEdit() {
      setAddressEditing(true);
    }
  }));
  useEffect(() => {
    setDefaultAddress({
      firstName: billingFormData?.firstName || userIdentity?.firstName || "",
      lastName: billingFormData?.lastName || userIdentity?.lastName || "",
      street: billingFormData?.street || userIdentity?.street || "",
      apartment: billingFormData?.apartment || userIdentity?.apartment || "",
      postalCode: billingFormData?.postalCode || userIdentity?.postalCode || "",
      city: billingFormData?.city || userIdentity?.city || "",
      countryId: marketCountry?.regionId + "",
      countryCode: marketCountry?.regionAbbr
    });
  }, [userIdentity, billingFormData, addAddressRef, marketCountry]);
  useEffect(() => {
    if (!noAddress) {
      let _selectedAddress;
      if (selectedAddress && selectedAddress.address) {
        _selectedAddress = addressList.find(x => x.value === selectedAddress.address?.addressId);
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList.find(x => x.isNew);
      }
      if (!_selectedAddress && billingFormData?.addressId) {
        _selectedAddress = addressList.find(x => x.value === billingFormData.addressId);
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList.find(x => x.address?.defaultFlag == "1");
      }
      if (!_selectedAddress) {
        _selectedAddress = addressList[0];
      }
      setSelectedAddress(_selectedAddress);
    }
  }, [addressList, noAddress, billingFormData]);
  const onAddressChange = address => {
    if (selectedAddress !== address) {
      setSelectedAddress(address);
      if (editAddress && editAddress !== address) {
        setEditAddress(null);
      }
      if (address.value !== "new") {
        setAddressEditing(false);
      } else {
        setDefaultAddress({
          firstName: userIdentity?.firstName || "",
          lastName: userIdentity?.lastName || ""
        });
      }
    }
  };
  const removeAddressModalProps = {
    ...removeAddressModal.fields,
    showModal: showRemoveAddressModal,
    onModalClose: evt => {
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    },
    onSubmit: async evt => {
      await api.address.delete(null, {
        addressId: addressToRemove.value
      });
      removeAddress(addressToRemove);
      setAddressToRemove(null);
      setShowRemoveAddressModal(false);
    }
  };
  const onEditClick = (evt, option) => {
    evt.stopPropagation();
    setEditAddress(option);
  };
  const onRemoveClick = (evt, option) => {
    evt.stopPropagation();
    setAddressToRemove(option);
    setShowRemoveAddressModal(true);
  };
  const addAddressFormProps = {
    fields,
    ref: addAddressRef,
    address: defaultAddress,
    savable: !noAddress,
    async onSaveSuccess(newAddress) {
      setEditAddress(null);
      const addedAddress = await addAddress(newAddress);
      setSelectedAddress(addedAddress);
      setAddressEditing(false);
    }
  };
  const ctaChangeAddressProps = ctaNameChange.value && {
    buttonText: ctaNameChange.value,
    ctaType: ctaTypeChange,
    colorScheme: colorSchemeChange,
    iconName: iconNameChange,
    callback(evt) {
      setAddressEditing(true);
    }
  };
  const shippingLayout = option => {
    if (option.address) {
      if (editAddress === option) {
        const editAddressFormProps = {
          fields,
          ref: editAddressRef,
          address: option.address,
          async onSaveSuccess() {
            const updatedAddress = await updateAddress(editAddress);
            setSelectedAddress(updatedAddress);
            setEditAddress(null);
            setAddressEditing(false);
          }
        };
        return (
          <div className="edit-address-block" key={option.address.id} onClick={e => e.stopPropagation()}>
            <p className="label">Edit Address</p>
            <PdpBillingInfoForm {...editAddressFormProps} />
          </div>
        );
      } else {
        return (
          <div className="saved-address-block" key={option.address.id}>
            <div className="body-container">
              {formatSavedAddress(option.address).map((row, index) => {
                return (
                  <p className="content-row" key={index}>
                    {row}
                  </p>
                );
              })}
            </div>
            <div className="icons">
              <Icon name="Edit" colorScheme="outline-green" className="icon --visible" onClick={evt => onEditClick(evt, option)} />
              {selectedAddress !== option && addressList.length > 2 && option.address.defaultFlag != "1" && <Icon name="Delete" colorScheme="outline-green" className="icon --visible" onClick={evt => onRemoveClick(evt, option)} />}
            </div>
          </div>
        );
      }
    } else {
      return <p className="label">{option.label}</p>;
    }
  };
  if (visible && addressLoaded) {
    return (
      <div className="billing-info" ref={componentRoot}>
        <div className="title">
          <Heading type={headingType} html={heading} />
        </div>
        {!addressEditing && selectedAddress && <AddressInformation className="default-address" address={selectedAddress.address} showCountry={false} />}
        {isLoggedIn() && addressEditing && <RadioButtonGroup options={addressList} html={shippingLayout} alignment="top" onChange={onAddressChange} selectedOption={selectedAddress} />}
        {(!isLoggedIn() || (selectedAddress && selectedAddress.value === "new") || noAddress) && <PdpBillingInfoForm {...addAddressFormProps} />}
        {!addressEditing && !noAddress && (
          <div className="cta-edit-address">
            <SuperButton {...ctaChangeAddressProps} />
          </div>
        )}
        {formError && <div className="failed-message">{formError}</div>}
        <AccountRemoveAddressModal {...removeAddressModalProps} />
      </div>
    );
  } else {
    return null;
  }
});
PdpBillingInfo.displayName = "BillingInfo";
export default PdpBillingInfo;
